import { H4, H5 } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { AbsenceTypeForm } from './absence-type-dialog'
import { cn } from '@/lib/utils'
import { getWeekdayName } from '@/utils'

type Preset = Exclude<AbsenceTypeForm['preset'], 'SIMPLE' | 'CUSTOM'>

const presetWorkDays: Record<Preset, number> = {
  SELF_CERTIFICATION_NO: 127,
  VACATION_NO: 31,
  SICK_CHILD_NO: 31,
  FLEXTIME: 127,
}

const presetSalaryDays: Record<Preset, number> = {
  SELF_CERTIFICATION_NO: 31,
  VACATION_NO: 31,
  SICK_CHILD_NO: 31,
  FLEXTIME: 127,
}

interface DayBoxProps {
  preset: Preset
  type: 'work' | 'salary'
  day: number
}

function DayBox({ preset, type, day }: DayBoxProps) {
  const presetDays = type === 'work' ? presetWorkDays : presetSalaryDays
  const isActive = (presetDays[preset] & (1 << day)) !== 0

  return (
    <div
      className={cn(
        'font-semibold text-sm',
        isActive ? 'text-green-600' : 'text-stone-300'
      )}
    >
      {getWeekdayName(day, 'md')}
    </div>
  )
}

interface Props {
  preset: Preset
}

export function AbsencePresetExplanation({ preset }: Props) {
  const translations = useTranslate({
    chosen: 'settings.absence.absence-type.preset-explanation.chosen-preset',
    details: 'settings.absence.absence-type.preset-explanation.details',
    workDays: 'settings.absence.absence-type.preset-explanation.work-days',
    workDaysDescription:
      'settings.absence.absence-type.preset-explanation.work-days-description',
    salaryDays: 'settings.absence.absence-type.preset-explanation.salary-days',
    salaryDaysDescription:
      'settings.absence.absence-type.preset-explanation.salary-days-description',

    presetNames: {
      VACATION_NO: 'settings.absence.absence-type.presets.vacation-no.title',
      SELF_CERTIFICATION_NO:
        'settings.absence.absence-type.presets.self-certification-no.title',
      SICK_CHILD_NO:
        'settings.absence.absence-type.presets.sick-child-no.title',
      FLEXTIME: 'settings.absence.absence-type.presets.flextime.title',
    },
    presetExplanations: {
      VACATION_NO:
        'settings.absence.absence-type.preset-explanation.vacation-no',
      SELF_CERTIFICATION_NO:
        'settings.absence.absence-type.preset-explanation.self-certification-no',
      SICK_CHILD_NO:
        'settings.absence.absence-type.preset-explanation.sick-child-no',
      FLEXTIME: 'settings.absence.absence-type.preset-explanation.flextime',
    },
    extraInfo: {
      SICK_CHILD_NO:
        'settings.absence.absence-type.preset-explanation.sick-child-no-extra',
    },
  })

  return (
    <div>
      <H4>
        <span className="text-stone-500 font-medium">
          {translations.chosen}
        </span>{' '}
        {translations.presetNames[preset]}
      </H4>

      <H5 className="mt-2">{translations.details}</H5>
      <p className="text-sm text-balance">
        {translations.presetExplanations[preset]}
      </p>

      {preset === 'SICK_CHILD_NO' && (
        <p className="text-sm text-balance mt-2">
          {translations.extraInfo.SICK_CHILD_NO}
        </p>
      )}

      <H5 className="mt-2">{translations.workDays}</H5>
      <p className="text-xs text-balance text-stone-600">
        {translations.workDaysDescription}
      </p>
      <div className="flex gap-2">
        {Array.from({ length: 7 }).map((_, day) => (
          <DayBox key={day} preset={preset} type="work" day={day} />
        ))}
      </div>

      <H5 className="mt-2">{translations.salaryDays}</H5>
      <p className="text-xs text-balance text-stone-600">
        {translations.salaryDaysDescription}
      </p>
      <div className="flex gap-2">
        {Array.from({ length: 7 }).map((_, day) => (
          <DayBox key={day} preset={preset} type="salary" day={day} />
        ))}
      </div>
    </div>
  )
}
